/**
 * Приведение адреса сайта к тому виду, в котором его принимает контракт.
 *
 * Зачем это здесь, а не на сервере. Человек вставляет адрес из строки браузера
 * — `https://seeno.ru`, — и это единственный способ, которым нормальный человек
 * знает свой сайт. Контракт же хранит домен и только домен:
 * `^[a-z0-9](?:[a-z0-9.-]{0,251}[a-z0-9])?$`, потому что по нему сходятся
 * цитирования, отличаются тёзки и считается видимость. Пока нормализации не
 * было, мастер честно нёс введённое на сервер и показывал «Домен должен быть
 * записан строчными буквами без протокола и пути» — на шаге 2, после разбора,
 * когда человек уже считал, что заполнил всё правильно. Ловило каждого, кто
 * копирует адрес, а не печатает его по памяти.
 *
 * Сервер проверку НЕ теряет: он остаётся последней инстанцией и отвечает 422 на
 * то, что клиент не смог привести (кириллический домен, мусор). Клиент лишь
 * перестаёт отправлять заведомо отвергаемое и показывает человеку то самое
 * значение, которое уйдёт на сервер, — иначе правка выглядит как подмена.
 */

/** Форма домена из контракта (`DOMAIN_SHAPE` в `entities/brands.py`). */
export const DOMAIN_SHAPE = /^[a-z0-9](?:[a-z0-9.-]{0,251}[a-z0-9])?$/;

/**
 * Вернуть чистый домен из того, что ввёл человек.
 *
 * Срезается всё, что не является именем узла: протокол, данные входа, путь,
 * запрос, якорь, порт и завершающие точки. Регистр приводится к нижнему,
 * пробелы по краям снимаются.
 *
 * Функция НЕ угадывает и не достраивает: `www` остаётся, потому что
 * `www.seeno.ru` — это законный домен, а не описка, и решать за клиента, какой
 * из двух его сайт, мы не вправе. Непреобразуемое возвращается как есть: пусть
 * сервер назовёт причину отказа своими словами, чем клиент увидит молча
 * изменённое до неузнаваемости поле.
 */
export function normalizeDomain(raw: string): string {
  let value = raw.trim();
  if (value === '') return '';
  // Протокол вместе с `//`, а также схемы без узла вида `mailto:`.
  value = value.replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, '');
  // Данные входа: `user:pass@host`.
  value = value.replace(/^[^/@]*@/, '');
  // Путь, запрос, якорь — всё, что начинается с первого `/`, `?` или `#`.
  value = value.split(/[/?#]/, 1)[0] ?? '';
  // Порт. Отрезается только числовой хвост: двоеточие в другом месте — это уже
  // не домен, и пусть его отвергнет сервер, а не наша догадка.
  value = value.replace(/:\d+$/, '');
  // Корневая точка `seeno.ru.` — законная запись в DNS, но не в контракте.
  value = value.replace(/\.+$/, '');
  return value.toLowerCase();
}

/** Пройдёт ли значение проверку контракта после приведения. */
export function isDomainShaped(value: string): boolean {
  return value.length <= 253 && DOMAIN_SHAPE.test(value);
}

/**
 * Почтовые службы, которые раздают ящики кому угодно: бесплатные и одноразовые.
 *
 * Решение фаундера 11 августа 2026: бесплатный аудит запускается по рабочему
 * адресу. Список закрытый и короткий намеренно — он отсекает очевидное, а не
 * пытается угадать все домены мира. Спорный случай проходит: отказать живому
 * покупателю на входе в воронку дороже, чем пропустить одного постороннего.
 *
 * Список неполон по определению, поэтому последнее слово за сервером: тот же
 * перечень обязан лежать в `seeno-ru-audit`, иначе запуск в обход формы, прямо
 * в API, не проверяется ничем.
 */
export const PUBLIC_MAIL_DOMAINS = [
  // Россия и СНГ
  'mail.ru',
  'inbox.ru',
  'list.ru',
  'bk.ru',
  'internet.ru',
  'yandex.ru',
  'yandex.com',
  'yandex.by',
  'yandex.kz',
  'ya.ru',
  'rambler.ru',
  'lenta.ru',
  'autorambler.ru',
  'myrambler.ru',
  'ro.ru',
  'pochta.ru',
  'nm.ru',
  'tut.by',
  'ukr.net',
  'i.ua',
  'meta.ua',
  'bigmir.net',
  'mail.kz',
  // Мир
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'yahoo.com',
  'ymail.com',
  'rocketmail.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'tutanota.com',
  'tuta.io',
  'gmx.com',
  'gmx.de',
  'gmx.net',
  'web.de',
  'zoho.com',
  'fastmail.com',
  'hushmail.com',
  'qq.com',
  '163.com',
  '126.com',
  'sina.com',
  'seznam.cz',
  'wp.pl',
  'o2.pl',
  'interia.pl',
  'abv.bg',
  'inbox.lv',
  // Одноразовые
  'mailinator.com',
  '10minutemail.com',
  'guerrillamail.com',
  'sharklasers.com',
  'temp-mail.org',
  'tempmail.com',
  'tempail.com',
  'yopmail.com',
  'trashmail.com',
  'throwawaymail.com',
  'dropmail.me',
  'maildrop.cc',
  'getnada.com',
  'emailondeck.com',
  'moakt.com',
  'mohmal.com',
  'mytemp.email',
  'spamgourmet.com',
  'fakemail.net',
] as const;

/**
 * Форма адреса. Нарочно грубая: точная проверка почты регулярным выражением —
 * известный способ отказать живому человеку с законным адресом. Здесь ловятся
 * только опечатки вида «нет собаки» и «нет точки в домене», всё остальное
 * подтверждает письмо, которое на этот адрес уходит.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Домен почтового адреса в нижнем регистре. Пусто, если адрес не разобрать. */
export function mailDomain(raw: string): string {
  const value = raw.trim().toLowerCase();
  const at = value.lastIndexOf('@');
  return at === -1 ? '' : value.slice(at + 1).replace(/\.+$/, '');
}

/** Похоже ли это на адрес почты вообще. */
export function isEmailShaped(raw: string): boolean {
  return EMAIL_SHAPE.test(raw.trim());
}

/**
 * Рабочий ли адрес: по форме похож на почту и не выдан публичной службой.
 *
 * Домен сравнивается целиком, а не по хвосту: `mail.ru` в списке не делает
 * посторонним `mail.rt.ru`, а `qq.com` — китайский корпоративный `sales.qq.com`
 * трогать не должен, поэтому проверяется точное совпадение и совпадение по
 * поддомену тех же служб (`inbox.mail.ru`).
 */
export function isWorkEmail(raw: string): boolean {
  if (!isEmailShaped(raw)) return false;
  const domain = mailDomain(raw);
  if (domain === '') return false;
  return !PUBLIC_MAIL_DOMAINS.some(
    (free) => domain === free || domain.endsWith(`.${free}`),
  );
}
